import { Clock, RefreshCw, Shield, MessageCircle } from 'lucide-react';
import { ScrollReveal } from '@/hooks/useScrollReveal';
import { useTilt } from '@/hooks/useEffects';

const PROMISES = [
  {
    icon: Clock,
    title: '24-Hour Response Time',
    description: 'Send me a message and you will hear back within a day. No chasing, no ghosting, no waiting around wondering what is happening.',
  },
  {
    icon: RefreshCw,
    title: 'Built Around Your Vision',
    description: 'Your project gets the attention it deserves. I work closely with you to make sure the final result feels right — not just "okay".',
  },
  {
    icon: Shield,
    title: 'No Hidden Fees, Ever',
    description: 'The price I quote is the price you pay. No surprise charges halfway through, no sneaky add-ons, no awkward conversations.',
  },
  {
    icon: MessageCircle,
    title: 'Plain English, Always',
    description: 'No jargon, no tech-speak, no confusing emails. I explain everything clearly so you always know exactly where your project stands.',
  },
];

function PromiseCard({ promise, i }) {
  const tilt = useTilt(5);
  return (
    <ScrollReveal delay={i * 100}>
      <div
        data-testid={`promise-card-${i}`}
        ref={tilt.ref}
        onMouseMove={tilt.onMouseMove}
        onMouseLeave={tilt.onMouseLeave}
        className="tilt-card group p-6 rounded-2xl bg-white dark:bg-[#111111] border border-slate-200/80 dark:border-white/10 hover:border-[#00E5FF]/30 glow-hover"
      >
        <div className="w-10 h-10 rounded-lg bg-[#0A0A0A] dark:bg-[#8B5CF6]/20 flex items-center justify-center mb-4 group-hover:bg-[#00E5FF] dark:group-hover:bg-[#8B5CF6] transition-colors duration-300">
          <promise.icon className="w-4 h-4 text-white group-hover:text-white transition-colors" />
        </div>
        <h3 className="font-['Outfit'] text-lg font-semibold tracking-tight text-[#0A0A0A] dark:text-white mb-2">
          {promise.title}
        </h3>
        <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400">
          {promise.description}
        </p>
      </div>
    </ScrollReveal>
  );
}

export default function PromiseSection() {
  return (
    <section id="promise" data-testid="promise-section" className="py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 gradient-line w-1/3" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <ScrollReveal>
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-slate-400 block mb-4">
                My Promise
              </span>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A] dark:text-white mb-6">
                What You Can
                <br />
                <span className="text-gradient">Count On</span>
              </h2>
              <p className="text-base leading-relaxed text-slate-600 dark:text-slate-400">
                I do not have years of client reviews to show you yet. What I do have is a clear set of commitments I stand behind on every single project.
              </p>
            </ScrollReveal>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {PROMISES.map((promise, i) => (
                <PromiseCard key={promise.title} promise={promise} i={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
